// BUILD-042 (Oct 24) - Guided initial HR setup: /hr/admin/employees/:employeeId/setup
//
// Authority: docs/superpowers/decisions/2026-10-24-build-042-guided-intake-admin-layout-local.md (+ four-image clarification).
// The step the person is on, HR's decisions and every summary come from the SERVER (GET .../setup): reload, reopen or sign-in again resumes at the saved step.
// Nothing about progress lives in localStorage. Cycle 12: HR may open ANY step in any order (rail or ?step= link); the server's recorded step stays the resume point.
// The shell owns the footer (Back | Save & exit ... [acknowledgment] Save & continue), the help box below it, and the once-per-visit "visited" mark.
// Opening this route for a person with no progress (a legacy record) or with finished setup sends the viewer to the normal employee page.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  finishHrSetup, getHrAdminEmployee, getHrSetup, listHrAdminEmployees, listHrDepartments, saveHrSetup,
  type HrSetupSaveBody, type HrSetupStep, type HrSetupView, type HrAdminEmployeeDetailResponse, type HrDepartment,
} from '../../../api/client';
import { Breadcrumbs } from '../workspace/Breadcrumbs';
import { classifyLoadError, TARGET_UNAVAILABLE_TEXT } from '../workspace/loadState';
import { formatLocalDate } from '../hrFormat';
import { SetupRail } from './SetupRail';
import { DetailsStep } from './DetailsStep';
import { BenefitsStep } from './BenefitsStep';
import { ChecklistStep } from './ChecklistStep';
import { DocumentsStep } from './DocumentsStep';
import { EquipmentStep } from './EquipmentStep';
import { ReviewStep } from './ReviewStep';
import { OUTLINE_BTN, PRIMARY_BTN } from './stepUi';
import { OWN_RECORD_MESSAGE } from './employeeDetails';
import {
  canFinishSetup, employeePath, nextStepOf, parseStepParam, previousStepOf, QUEUE_PATH, resolveStep, setupFailure, setupPath, SETUP_STEPS, stepDef, stepIndex, stepStandings, stepToStore,
  type SetupFailure,
} from './setupModel';
import type { StepContext, StepFooterSpec, StepSaver, PersonInfo } from './setupTypes';

type LoadState = { kind: 'loading' } | { kind: 'denied' } | { kind: 'target' } | { kind: 'unavailable'; message: string } | { kind: 'ready' };
type Busy = null | 'continue' | 'exit' | 'finish';

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}
function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function SetupWizard({ viewerEmployeeId }: { viewerEmployeeId: string }) {
  const { employeeId = '' } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [view, setView] = useState<HrSetupView | null>(null);
  const [record, setRecord] = useState<HrAdminEmployeeDetailResponse | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [departments, setDepartments] = useState<HrDepartment[]>([]);

  const [busy, setBusy] = useState<Busy>(null);
  const [failure, setFailure] = useState<{ message: string; saved?: string; notSaved?: string } | null>(null);
  const [finishFailure, setFinishFailure] = useState<SetupFailure | null>(null);
  const [dirty, setDirty] = useState(false);
  const [backWarning, setBackWarning] = useState(false);

  const saverRef = useRef<StepSaver | null>(null);
  // The step's footer description lives in a ref (read at render and at click, so handlers are never stale); state only changes when what is VISIBLE changes,
  // so a step that re-binds on every render cannot cause a render loop.
  const footerRef = useRef<{ step: HrSetupStep; spec: StepFooterSpec } | null>(null);
  const [, setFooterSig] = useState('');
  const visitedRef = useRef<Set<string>>(new Set());
  const viewRef = useRef<HrSetupView | null>(null);
  viewRef.current = view;
  const activeRef = useRef<HrSetupStep>('details');
  const timers = useRef<number[]>([]);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; timers.current.forEach((t) => window.clearTimeout(t)); }; }, []);

  // ── Server reads ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const reload = useCallback(async () => {
    if (!employeeId) return;
    try {
      const v = await getHrSetup(employeeId);
      if (!alive.current) return;
      setView(v);
      setLoad({ kind: 'ready' });
    } catch (e: any) {
      if (!alive.current) return;
      // A failed REFRESH keeps what is already on screen; only the first load can fail the page.
      if (viewRef.current) return;
      const k = classifyLoadError(e, { targeted: true });
      setLoad(k.kind === 'denied' ? { kind: 'denied' } : k.kind === 'targetUnavailable' ? { kind: 'target' } : { kind: 'unavailable', message: k.kind === 'unavailable' ? k.message : 'The request failed.' });
    }
  }, [employeeId]);

  useEffect(() => {
    setView(null); setLoad({ kind: 'loading' }); setRecord(null); setEmail(null);
    void reload();
    getHrAdminEmployee(employeeId).then((r) => alive.current && setRecord(r)).catch(() => { /* the name falls back below; setup itself still works */ });
    listHrAdminEmployees().then((d) => { if (alive.current) setEmail(d.employees.find((x) => x.employeeId === employeeId)?.email ?? null); }).catch(() => { /* shown as "Not available" */ });
    listHrDepartments({ includeDisabled: true }).then((d) => alive.current && setDepartments(d.departments)).catch(() => { /* review shows ids as unassigned only if unknown */ });
  }, [employeeId, reload]);

  const reloadSoon = useCallback(() => {
    for (const ms of [900, 2600]) timers.current.push(window.setTimeout(() => { if (alive.current) void reload(); }, ms));
  }, [reload]);

  // ── Routing rules: legacy / finished => the normal page (replace, so there is no loop and Back works) ─────────────────────────
  useEffect(() => {
    if (load.kind === 'ready' && view && (!view.progress || view.progress.status === 'finished')) navigate(employeePath(employeeId), { replace: true });
  }, [load, view, employeeId, navigate]);

  const serverStep: HrSetupStep = view?.progress?.step ?? 'details';
  const requested = parseStepParam(params.get('step'));
  const active = resolveStep(requested, serverStep);
  activeRef.current = active;
  const fromReview = params.get('from') === 'review';
  // A different step starts clean.
  useEffect(() => { setFailure(null); setFinishFailure(null); setDirty(false); setBackWarning(false); }, [active, employeeId]);

  // ── Visit tracking: opening a step marks it visited ONCE per visit (a visit alone resolves nothing and never moves the resume point) ─────────────────
  useEffect(() => {
    const p = view?.progress;
    if (load.kind !== 'ready' || !p || p.status !== 'in_progress' || active === 'review') return;
    const key = `${employeeId}:${active}`;
    if (p.flags?.[active]?.visited) { visitedRef.current.add(key); return; }
    if (visitedRef.current.has(key)) return;
    visitedRef.current.add(key);
    saveHrSetup(employeeId, { step: active, visited: true, nextStep: p.step })
      .then(() => { if (alive.current) void reload(); })
      .catch(() => { /* a visit mark is a convenience; the step itself still works and the next visit tries again */ visitedRef.current.delete(key); });
  }, [load.kind, view, active, employeeId, reload]);

  // ── Derived display ──────────────────────────────────────────────────────────────────────────────────────────────────────────
  const name = record?.name ?? null;
  const person: PersonInfo = { name: name ?? 'This person', email };
  const nameKnown = !!name;
  const standings = useMemo(() => stepStandings(view?.progress ?? null, view?.summary ?? null), [view]);
  const finishable = canFinishSetup(view?.progress ?? null, view?.summary ?? null);
  const hireDate = view?.summary.details.hireDate ?? record?.period?.hireDate ?? null;
  const badge = record?.period ? `${titleCase(record.period.employmentStatus)}${hireDate ? ` · Hired ${formatLocalDate(hireDate)}` : ''}` : hireDate ? `Hired ${formatLocalDate(hireDate)}` : null;
  const departmentName = useCallback((id: string | null) => (id ? departments.find((d) => d.departmentId === id)?.name ?? null : null), [departments]);

  // ── Saving ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const saveDecisions: StepContext['saveDecisions'] = useCallback(async (d) => {
    const v = viewRef.current;
    if (!v?.progress) return { ok: false, message: 'Setup is not open for this person.' };
    try {
      // nextStep keeps the server's step where it is: recording a decision on an earlier step must not move progress backwards.
      await saveHrSetup(employeeId, { step: activeRef.current, decisions: d, nextStep: v.progress.step });
      await reload();
      return { ok: true };
    } catch (e) {
      return { ok: false, message: setupFailure(e, 'The decision could not be saved.').message };
    }
  }, [employeeId, reload]);

  const bindFooter = useCallback((spec: StepFooterSpec | null) => {
    footerRef.current = spec ? { step: activeRef.current, spec } : null;
    const sig = spec ? JSON.stringify([spec.ack ? [spec.ack.id, spec.ack.label, spec.ack.checked, !!spec.ack.disabled, spec.ack.hint ?? ''] : null, spec.help?.title ?? null, spec.invalidMessage ?? null]) : '';
    setFooterSig((prev) => (prev === sig ? prev : sig));
  }, []);

  /** continue = save valid entries, then go to the next step (an unresolved step stays amber "Needs attention") | exit = save, then leave. */
  async function persist(kind: 'continue' | 'exit') {
    if (busy || !view?.progress) return;
    setFinishFailure(null);
    const invalid = footerRef.current?.step === active ? footerRef.current.spec.invalidMessage : null;
    // Entries the step says are invalid are never saved or discarded: say so and stay.
    if (invalid) { setFailure({ message: invalid, notSaved: 'the entries that need fixing' }); return; }
    setBusy(kind); setFailure(null);
    const saver = saverRef.current;
    let decisionsPatch: HrSetupSaveBody['decisions'];
    if (saver) {
      let r;
      try { r = await saver(); } catch (e: any) { r = { ok: false as const, message: e?.message || 'The step could not be saved.' }; }
      if (!r.ok) { setFailure({ message: r.message, saved: r.saved, notSaved: r.notSaved }); setBusy(null); await reload(); return; }
      decisionsPatch = r.decisions;
    }
    const cur = viewRef.current?.progress?.step ?? serverStep;
    const next: HrSetupStep = kind === 'exit'
      ? (stepIndex(cur) >= stepIndex(active) ? cur : active)
      : (fromReview ? 'review' : stepToStore(active, cur));
    try {
      await saveHrSetup(employeeId, { step: active, ...(decisionsPatch ? { decisions: decisionsPatch } : {}), nextStep: next });
    } catch (e) {
      const f = setupFailure(e, 'Your place in setup could not be saved.');
      const action = kind === 'exit' ? 'Save & exit' : 'Save & continue';
      setFailure({
        message: saver
          ? `Your entries on this step were saved, but ${decisionsPatch ? 'the step could not be confirmed and your place in setup was not recorded' : 'your place in setup could not be recorded'}: ${f.message} Use ${action} again.`
          : `Your place in setup could not be recorded: ${f.message}`,
        saved: saver ? 'this step’s entries' : undefined,
        notSaved: decisionsPatch ? 'your confirmation and your place in setup' : 'your place in setup',
      });
      setBusy(null); await reload(); return;
    }
    if (!alive.current) return;
    setDirty(false);
    if (kind === 'exit') { navigate(QUEUE_PATH); return; }
    await reload();
    setBusy(null);
    navigate(setupPath(employeeId, fromReview ? 'review' : nextStepOf(active)));
  }

  async function finish() {
    if (busy || !view?.progress) return;
    setBusy('finish'); setFailure(null); setFinishFailure(null);
    try {
      await finishHrSetup(employeeId);
      if (!alive.current) return;
      navigate(employeePath(employeeId), { replace: true, state: { setupFinished: true } });
    } catch (e) {
      setFinishFailure(setupFailure(e, 'Setup could not be finished.'));
      await reload();
      setBusy(null);
    }
  }

  function goBack() {
    const prev = previousStepOf(active);
    if (!prev) return;
    if (dirty && !backWarning) { setBackWarning(true); return; }
    navigate(setupPath(employeeId, prev));
  }
  function openStep(step: HrSetupStep) {
    if (dirty && !backWarning) { setBackWarning(true); return; }
    navigate(setupPath(employeeId, step));
  }

  // ── Render ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const crumbs = [
    { label: 'HR', to: '/hr/admin' }, { label: 'Employees', to: '/hr/admin' },
    { label: name ?? 'Employee', to: employeePath(employeeId) }, { label: 'Setup' },
  ];

  if (employeeId && employeeId === viewerEmployeeId) {
    return (
      <div data-testid="setup-own-record">
        <Breadcrumbs items={crumbs} />
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 max-w-2xl">
          <h1 className="text-xl font-semibold text-gray-900">HR setup isn’t available on your own record</h1>
          <p className="mt-2 text-sm text-gray-700">{OWN_RECORD_MESSAGE}</p>
          <Link to="/hr/admin" className="inline-block mt-4 text-sm text-[#176A9B] hover:underline">← Back to employees</Link>
        </div>
      </div>
    );
  }
  if (load.kind === 'loading') {
    return <div className="text-gray-500" role="status" data-testid="setup-loading">Loading HR setup…</div>;
  }
  if (load.kind !== 'ready' || !view) {
    const text = load.kind === 'denied' ? 'You don’t have permission to open HR setup. It needs the “HR management” permission (hr.org.manage) over this person. Nothing was changed.'
      : load.kind === 'target' ? TARGET_UNAVAILABLE_TEXT
      : `HR setup could not be loaded${load.kind === 'unavailable' && load.message ? `: ${load.message}` : '.'} This is not the same as setup being finished or not needed.`;
    return (
      <div data-testid="setup-load-problem">
        <Breadcrumbs items={crumbs} />
        <div role="alert" className="bg-white rounded-lg border border-red-200 shadow-sm p-6 max-w-2xl">
          <h1 className="text-lg font-semibold text-gray-900">HR setup can’t be shown</h1>
          <p className="mt-2 text-sm text-gray-700">{text}</p>
          <div className="mt-4 flex gap-3">
            {load.kind === 'unavailable' && <button type="button" className={PRIMARY_BTN} onClick={() => { setLoad({ kind: 'loading' }); void reload(); }} data-testid="setup-retry">Try again</button>}
            <Link to="/hr/admin" className={OUTLINE_BTN}>Back to employees</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!view.progress || view.progress.status === 'finished') {
    return <div className="text-gray-500" role="status">Opening the employee…</div>; // the redirect effect above is already on its way
  }

  const def = stepDef(active);
  const who = nameKnown ? firstName(name!) : null;
  const heading = active === 'review' ? (who ? `Review ${who}’s HR setup` : 'Review HR setup') : (who ? `Set up ${who}’s HR record` : 'Set up the HR record');
  const sub = active === 'review' ? 'Check the saved information and assignments before finishing.' : `${def.label} • Step ${def.number} of ${SETUP_STEPS.length}`;

  const ctx: StepContext = {
    employeeId, person, view, reload, reloadSoon,
    bindSaver: (s) => { saverRef.current = s; },
    setDirty,
    saveDecisions,
    bindFooter,
  };
  const stepEl = active === 'details' ? <DetailsStep ctx={ctx} isOwnRecord={false} />
    : active === 'benefits' ? <BenefitsStep ctx={ctx} />
    : active === 'checklist' ? <ChecklistStep ctx={ctx} />
    : active === 'documents' ? <DocumentsStep ctx={ctx} />
    : active === 'equipment' ? <EquipmentStep ctx={ctx} />
    : <ReviewStep ctx={ctx} finishFailure={finishFailure} departmentName={departmentName} />;
  const isReview = active === 'review';
  const footerSpec = footerRef.current && footerRef.current.step === active ? footerRef.current.spec : null;
  const ack = footerSpec?.ack;

  return (
    <div data-testid="setup-wizard" data-step={active}>
      <Breadcrumbs items={crumbs} />
      <div className="flex flex-col lg:flex-row gap-5">
        <SetupRail name={name ?? 'Employee'} employeeId={employeeId} badge={badge} active={active} standings={standings} onSelect={openStep} />
        <main className="flex-1 min-w-0 space-y-4 max-w-4xl">
          <header>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-testid="setup-eyebrow">Guided HR setup</div>
            <h1 className="text-2xl font-semibold text-gray-900 mt-1" data-testid="setup-heading">{heading}</h1>
            <p className="text-sm text-gray-600 mt-0.5" data-testid="setup-subheading">{sub}</p>
          </header>

          {stepEl}

          {(failure || backWarning || dirty) && (
            <div className="space-y-2">
              {failure && (
                <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="save-failure">
                  <div className="font-semibold">Not saved</div>
                  <p className="text-xs mt-0.5">{failure.message}</p>
                  {(failure.saved || failure.notSaved) && (
                    <p className="text-xs mt-1" data-testid="save-failure-detail">
                      {failure.saved && <>Saved: {failure.saved}. </>}{failure.notSaved && <>Not saved: {failure.notSaved}. </>}Your entries are still here.
                    </p>
                  )}
                </div>
              )}
              {backWarning && (
                <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="back-warning">
                  You have changes on this step that are not saved. Choose Save &amp; continue to keep them, or go back again to leave without saving.
                </div>
              )}
              {dirty && !failure && <div className="text-xs text-amber-800" data-testid="unsaved-chip">● Unsaved changes on this step</div>}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3" data-testid="setup-footer">
            <div className="flex flex-wrap items-center gap-2">
              {isReview || previousStepOf(active) ? (
                <button type="button" className={OUTLINE_BTN} onClick={goBack} disabled={!previousStepOf(active) || !!busy} data-testid="setup-back">← Back</button>
              ) : null}
              <button type="button" className={OUTLINE_BTN} onClick={() => persist('exit')} disabled={!!busy} data-testid="setup-save-exit">{busy === 'exit' ? 'Saving…' : 'Save & exit'}</button>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              {isReview ? (
                <>
                  {!finishable && <span id="setup-finish-caption" className="text-xs text-gray-600" data-testid="setup-finish-caption">Resolve the flagged setup decision to finish.</span>}
                  <button type="button" className={PRIMARY_BTN} onClick={finish} disabled={!!busy || !finishable} aria-describedby={!finishable ? 'setup-finish-caption' : undefined} data-testid="setup-finish">{busy === 'finish' ? 'Finishing…' : 'Finish HR setup'}</button>
                </>
              ) : (
                <>
                  {ack && (
                    <div className="flex flex-col" data-testid="setup-ack">
                      <label htmlFor={ack.id} className="inline-flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                        <input
                          type="checkbox" id={ack.id} className="h-4 w-4 flex-shrink-0 accent-[#2F7FB8]"
                          checked={ack.checked} disabled={!!ack.disabled || !!busy}
                          aria-describedby={ack.hint ? `${ack.id}-hint` : undefined}
                          onChange={(e) => footerRef.current?.spec.ack?.onChange(e.target.checked)}
                          data-testid="setup-ack-checkbox"
                        />
                        <span>{ack.label}</span>
                      </label>
                      {ack.hint && <span id={`${ack.id}-hint`} className="ml-6 text-xs text-gray-500" data-testid="setup-ack-hint">{ack.hint}</span>}
                    </div>
                  )}
                  <button type="button" className={PRIMARY_BTN} onClick={() => persist('continue')} disabled={!!busy} data-testid="setup-save-continue">{busy === 'continue' ? 'Saving…' : fromReview ? 'Save & return to review' : 'Save & continue →'}</button>
                </>
              )}
            </div>
          </div>

          {footerSpec?.help && (
            <details key={active} className="group bg-white rounded-lg border border-gray-200 shadow-sm" data-testid="setup-help">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-800 marker:text-gray-500">{footerSpec.help.title}</summary>
              <div className="px-4 pb-4 text-sm text-gray-700" data-testid="setup-help-body">{footerSpec.help.body}</div>
            </details>
          )}
        </main>
      </div>
    </div>
  );
}
